/**
 * `<caos-validation-summary>` — every blocking error on one save, each linked to its
 * field. (Catalog: Status & feedback › ValidationSummary — every blocking error on one save, each linked to its field.)
 *
 * A danger-intent summary shown when a save is rejected: an icon + a heading ("There
 * are N problems to fix") over a list of the field errors. It reads the SEMANTIC danger
 * tokens — `--caos-color-danger-bg` for the fill, and the theme-fixed on-tint INK
 * (`--caos-color-danger-text` for the heading and each error link, `-text-muted` for the
 * field label) rather than the neutral text token, so it stays ≥4.5:1 legible on the
 * pale fill in BOTH light and dark (the AF2 contrast lesson). Errors come from the
 * `items` property (`{ message, field?, target? }[]`); each renders as a real link/button
 * that, on click, focuses (and scrolls to) the element named by `target` (an id in the
 * host's document), emitting a composed `caos-validation-focus` (detail `{ target, field }`).
 * The container is a `role="alert"` labelled by its heading, so assistive tech announces
 * the failure. Styling seams: `::part(summary)`, `::part(icon)`, `::part(title)`,
 * `::part(list)`, `::part(error)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

export interface CaosValidationItem {
  message: string;
  field?: string;
  target?: string;
}

let uid = 0;

const sheet = makeSheet(`
  :host { display: block; }

  .summary {
    display: flex;
    align-items: flex-start;
    gap: var(--caos-space-3);
    padding: var(--caos-space-3) var(--caos-space-4);
    border-radius: var(--caos-radius);
    background: var(--caos-color-danger-bg);
    border: 1px solid color-mix(in srgb, var(--caos-color-danger) 35%, transparent);
    /* body/label text uses the theme-fixed on-tint muted ink */
    color: var(--caos-color-danger-text-muted);
    font-family: var(--caos-font);
  }

  .icon { flex: none; width: 18px; height: 18px; margin-top: 1px; color: var(--caos-color-danger); }
  .icon svg { display: block; width: 100%; height: 100%; }

  .content { flex: 1 1 auto; min-width: 0; }
  .title {
    font-size: var(--caos-text-row);
    font-weight: 600; /* summary-title emphasis — off-scale weight, stays literal */
    line-height: 1.4;
    /* darker on-tint ink for the heading */
    color: var(--caos-color-danger-text);
    margin-bottom: var(--caos-space-2);
  }

  .list { margin: 0; padding-left: var(--caos-space-4); display: flex; flex-direction: column; gap: 3px; }
  .list li { font-size: 13px; line-height: 1.5; }
  .list li::marker { color: var(--caos-color-danger); }

  .error {
    appearance: none;
    background: transparent;
    border: none;
    padding: 0;
    font: inherit;
    font-size: 13px;
    text-align: left;
    color: var(--caos-color-danger-text);
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  .error:hover { text-decoration-thickness: 2px; }
  .error:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }
  .error[data-static] { text-decoration: none; cursor: default; color: inherit; }
  .field { font-weight: 600; color: var(--caos-color-danger-text-muted); }
  .field::after { content: ": "; }
`);

export class CaosValidationSummary extends CaosElement {
  static get observedAttributes(): string[] {
    return ['title'];
  }

  private summary: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private list: HTMLUListElement;
  private _items: CaosValidationItem[] = [];
  private readonly seq = ++uid;

  constructor() {
    super(sheet);

    this.summary = document.createElement('div');
    this.summary.className = 'summary';
    this.summary.setAttribute('part', 'summary');
    this.summary.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'icon';
    icon.setAttribute('part', 'icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.append(makeIcon('danger'));

    const content = document.createElement('div');
    content.className = 'content';
    this.titleEl = document.createElement('div');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    this.titleEl.id = `caos-validation-title-${this.seq}`;
    this.list = document.createElement('ul');
    this.list.className = 'list';
    this.list.setAttribute('part', 'list');
    content.append(this.titleEl, this.list);

    this.summary.setAttribute('aria-labelledby', this.titleEl.id);
    this.summary.append(icon, content);
    this.root.append(this.summary);
  }

  connectedCallback(): void {
    if (!this._items.length) this.readLightDom();
    this.render();
  }

  attributeChangedCallback(): void {
    this.renderTitle();
  }

  /** The field errors (data in). */
  get items(): CaosValidationItem[] {
    return this._items;
  }
  set items(list: CaosValidationItem[]) {
    this._items = list ?? [];
    this.render();
  }

  private readLightDom(): void {
    const nodes = Array.from(this.querySelectorAll('li'));
    if (nodes.length) {
      this._items = nodes.map((n) => ({
        message: (n.textContent ?? '').trim(),
        field: n.dataset.field,
        target: n.dataset.target,
      }));
    }
  }

  private renderTitle(): void {
    const n = this._items.length;
    const override = this.getAttribute('title');
    this.titleEl.textContent =
      override ?? (n === 1 ? 'There is 1 problem to fix' : `There are ${n} problems to fix`);
  }

  private render(): void {
    this.renderTitle();
    this.list.replaceChildren();
    this._items.forEach((it) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'error';
      btn.setAttribute('part', 'error');
      if (it.field) {
        const label = document.createElement('span');
        label.className = 'field';
        label.textContent = it.field;
        btn.append(label);
      }
      btn.append(document.createTextNode(it.message));
      if (it.target) {
        btn.addEventListener('click', () => this.focusTarget(it.target as string, it.field));
      } else {
        btn.setAttribute('data-static', '');
        btn.tabIndex = -1;
      }
      li.append(btn);
      this.list.append(li);
    });
  }

  private focusTarget(target: string, field?: string): void {
    this.dispatchEvent(
      new CustomEvent('caos-validation-focus', {
        bubbles: true,
        composed: true,
        detail: { target, field },
      }),
    );
    // best-effort: focus & scroll the field by id in the host document
    const doc = this.getRootNode() as Document | ShadowRoot;
    const el =
      (doc as Document).getElementById?.(target) ?? document.getElementById(target);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.focusField(el as HTMLElement);
    }
  }

  /**
   * Move focus onto the field. Native controls focus directly; a wrapper element (e.g.
   * a `caos-*-field` whose real control is in its open shadow root, or a light-DOM
   * container) delegates to its first focusable descendant so the caret actually lands.
   */
  private focusField(el: HTMLElement): void {
    const sel = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    if (el.matches(sel)) {
      el.focus();
      return;
    }
    const inner =
      el.querySelector<HTMLElement>(sel) ?? el.shadowRoot?.querySelector<HTMLElement>(sel) ?? null;
    (inner ?? el).focus?.();
  }
}

define('caos-validation-summary', CaosValidationSummary);

declare global {
  interface HTMLElementTagNameMap {
    'caos-validation-summary': CaosValidationSummary;
  }
}
